import { useEffect, useState } from 'react'
import { api, fmt } from '../lib/api.js'
import { Chip, Empty, ErrorNote } from '../components/ui.jsx'

// The instructions that actually drive the research and the drafting.
//
// Editing never writes into the research bundle. An edit is stored locally as
// an override, the original stays on disk, and Revert puts it back.
export default function Prompts() {
  const [list, setList] = useState(null)
  const [open, setOpen] = useState(null)
  const [text, setText] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const load = () => api.prompts().then(setList).catch(setError)
  useEffect(() => {
    load()
  }, [])

  const openPrompt = async (id) => {
    setError(null)
    try {
      const p = await api.prompt(id)
      setOpen(p)
      setText(p.text || '')
    } catch (err) {
      setError(err)
    }
  }

  const save = async () => {
    setBusy(true)
    try {
      const p = await api.savePrompt(open.id, text)
      setOpen(p)
      setText(p.text || '')
      await load()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  const revert = async () => {
    setBusy(true)
    try {
      const p = await api.revertPrompt(open.id)
      setOpen(p)
      setText(p.text || '')
      await load()
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  if (!list) return <div className="body"><Empty>Loading…</Empty></div>

  const dirty = open && text !== (open.text || '')

  return (
    <>
      <div className="topbar">
        <h1>Prompts</h1>
        <span className="topbar-note">{list.length} · {list.filter((p) => p.edited).length} edited</span>
        {open ? (
          <div className="topbar-right">
            {open.edited ? <Chip kind="replied">EDITED {fmt.date(open.edited_at)}</Chip> : null}
            <button className="btn" onClick={revert} disabled={busy || !open.edited}>Revert to original</button>
            <button className="btn primary" onClick={save} disabled={busy || !dirty}>Save</button>
          </div>
        ) : null}
      </div>

      <div className="body split">
        <div className="col" style={{ maxWidth: 320, flex: '0 0 320px' }}>
          <ErrorNote error={error} />
          <div className="card">
            <div className="card-header"><span className="card-title">What runs where</span></div>
            <div>
              {list.map((p) => (
                <button
                  key={p.id}
                  onClick={() => openPrompt(p.id)}
                  style={{
                    display: 'block',
                    width: '100%',
                    textAlign: 'left',
                    padding: '10px 14px',
                    border: 'none',
                    borderBottom: '0.5px solid var(--line-soft)',
                    borderLeft: `2px solid ${open?.id === p.id ? 'var(--accent)' : 'transparent'}`,
                    background: open?.id === p.id ? 'var(--accent-soft)' : 'var(--panel)',
                    fontFamily: 'var(--sans)',
                  }}
                >
                  <div className="row" style={{ alignItems: 'baseline' }}>
                    <span style={{ fontWeight: 500, color: open?.id === p.id ? 'var(--accent-ink)' : 'var(--ink)' }}>{p.label}</span>
                    {p.edited ? <Chip kind="replied">EDITED</Chip> : null}
                    {p.missing ? <Chip kind="stale">MISSING</Chip> : null}
                  </div>
                  <div style={{ fontSize: 'var(--fs-pill)', color: 'var(--muted)', marginTop: 2 }}>{p.used_for}</div>
                  <div className="mono" style={{ fontSize: 'var(--fs-badge)', color: 'var(--tertiary)', marginTop: 3 }}>
                    {p.source} · {p.length.toLocaleString('en-GB')} chars
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="notice" style={{ marginTop: 12, display: 'block' }}>
            <div className="notice-title">Edits do not touch the bundle</div>
            <div className="notice-body" style={{ marginTop: 3 }}>
              The research folder stays exactly as the research left it. An edit is stored beside your engagement data
              and used at run time; Revert throws it away.
            </div>
          </div>
        </div>

        <div className="col" style={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          {!open ? (
            <Empty>Pick a prompt to read or edit it.</Empty>
          ) : (
            <div className="card" style={{ flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <div className="card-header">
                <span className="card-title">{open.label}</span>
                <span className="card-sub">{open.source}</span>
              </div>
              <div style={{ padding: 14, flex: 1, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
                <textarea
                  className="input mono"
                  style={{ flex: 1, minHeight: 320, fontSize: 'var(--fs-pill)', lineHeight: 1.55, background: 'var(--panel)' }}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  spellCheck={false}
                />
                <div className="row" style={{ marginTop: 8 }}>
                  <span className="card-sub">
                    {text.length.toLocaleString('en-GB')} chars
                    {dirty ? ' · unsaved changes' : ''}
                  </span>
                  <span className="card-sub" style={{ marginLeft: 'auto' }}>
                    Used for: {open.used_for}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
